import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useCallback, useMemo } from "react"
import { useForm, useWatch } from "react-hook-form"

import { useWarehouseShelvingOptions } from "../hooks/useWarehouseShelvingOptions"
import {
  type DestinationOverride,
  type OverrideDestinationFormValues,
  overrideDestinationFormSchema,
} from "../schemas/overrideDestinationSchema"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FormAutocomplete } from "@/components/UiKit"
import { AerosSelect } from "@/components/UiKit/AerosSelect"
import { BIN_SIZE_OPTIONS } from "@/constants/warehouse"
import type { GoodsReceiptPallet, WarehouseArea, WarehouseShelving } from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"
import { classNames } from "@/utils/utils"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface GoodsReceiptPalletDestinationOverrideDialogProps {
  pallet: GoodsReceiptPallet
  warehouse: WarehouseArea
  pallets?: GoodsReceiptPallet[]
  destinationOverrides?: Map<string, DestinationOverride>
  onClose: () => void
  onSubmit: (destination: DestinationOverride) => void
}

export const GoodsReceiptPalletDestinationOverrideDialog: FC<GoodsReceiptPalletDestinationOverrideDialogProps> = ({
  pallet,
  warehouse,
  pallets,
  destinationOverrides,
  onClose,
  onSubmit,
}) => {
  const { isTabletView } = useAppView()

  const defaultFormValues = useMemo<OverrideDestinationFormValues>(
    () => ({
      binSize:
        destinationOverrides?.get(pallet.goodsReceiptPalletId)?.binSize ??
        pallet.destinationWarehouseShelving?.binSize ??
        null,
      aisleCode:
        destinationOverrides?.get(pallet.goodsReceiptPalletId)?.aisleCode ??
        pallet.destinationWarehouseShelving?.aisleCode ??
        "",
      sectionCode:
        destinationOverrides?.get(pallet.goodsReceiptPalletId)?.sectionCode ??
        pallet.destinationWarehouseShelving?.sectionCode ??
        "",
      binNumber:
        destinationOverrides?.get(pallet.goodsReceiptPalletId)?.binNumber ??
        pallet.destinationWarehouseShelving?.binNumber ??
        0,
    }),
    [destinationOverrides, pallet.goodsReceiptPalletId, pallet.destinationWarehouseShelving]
  )

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid },
  } = useForm<OverrideDestinationFormValues>({
    resolver: zodResolver(overrideDestinationFormSchema),
    mode: "all",
    defaultValues: defaultFormValues,
  })

  const binSize = useWatch({ control, name: "binSize" })
  const aisleCode = useWatch({ control, name: "aisleCode" })
  const sectionCode = useWatch({ control, name: "sectionCode" })

  const currentDestination = useMemo(
    () => destinationOverrides?.get(pallet.goodsReceiptPalletId) ?? pallet.destinationWarehouseShelving ?? null,
    [destinationOverrides, pallet.goodsReceiptPalletId, pallet.destinationWarehouseShelving]
  )

  const { aisleOptions, shelfOptions, binOptions, shelvings, isFetching } = useWarehouseShelvingOptions({
    warehouseId: warehouse.warehouseAreaId,
    binSize,
    aisleCode,
    sectionCode,
    currentPalletId: pallet.goodsReceiptPalletId,
    pallets,
    destinationOverrides,
    currentDestination: currentDestination as Partial<WarehouseShelving>,
  })

  const handleBinSizeChange = useCallback(() => {
    setValue("aisleCode", "", { shouldValidate: true })
    setValue("sectionCode", "")
    setValue("binNumber", 0)
  }, [setValue])

  const handleAisleCodeChange = useCallback(() => {
    setValue("sectionCode", "", { shouldValidate: true })
    setValue("binNumber", 0)
  }, [setValue])

  const handleSectionCodeChange = useCallback(() => {
    setValue("binNumber", 0, { shouldValidate: true })
  }, [setValue])

  const handleClose = useCallback(() => {
    reset(defaultFormValues)
    onClose()
  }, [reset, defaultFormValues, onClose])

  const handleFormSubmit = useCallback(
    (data: OverrideDestinationFormValues) => {
      // Find the warehouse shelving ID for the selected location
      const selectedShelving = shelvings.find(
        (s) => s.aisleCode === data.aisleCode && s.sectionCode === data.sectionCode && s.binNumber === data.binNumber
      )

      if (!selectedShelving?.warehouseShelvingId) {
        console.error("Could not find warehouse shelving ID for selected location")
        return
      }

      onSubmit({
        warehouseShelvingId: selectedShelving.warehouseShelvingId,
        aisleCode: data.aisleCode,
        sectionCode: data.sectionCode,
        binNumber: data.binNumber,
        binSize: data.binSize ?? undefined,
      })
      onClose()
    },
    [onSubmit, onClose, shelvings]
  )

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth={!isTabletView && "sm"}
      fullWidth={!isTabletView}
      fullScreen={isTabletView}
      data-testid='override-destination-dialog'
    >
      <DialogHeader title='Override Location' onClose={handleClose} />
      <form className='flex flex-1 flex-col' onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent className='flex flex-col gap-6 p-10'>
          <AerosSelect
            label='Warehouse'
            value={warehouse.warehouseAreaId}
            options={[{ value: warehouse.warehouseAreaId, label: formatWarehouseLabel(warehouse) }]}
            disabled
            data-testid='override-destination-warehouse-select'
          />

          <FormAutocomplete
            control={control}
            name='binSize'
            label='Bin Size'
            placeholder='Select bin size'
            options={BIN_SIZE_OPTIONS}
            onChange={handleBinSizeChange}
            data-testid='override-destination-bin-size-select'
          />

          <FormAutocomplete
            control={control}
            name='aisleCode'
            label='Aisle'
            placeholder='Select aisle'
            options={aisleOptions}
            onChange={handleAisleCodeChange}
            disabled={isFetching}
            noOptionsText='No aisles'
            required
            data-testid='override-destination-aisle-select'
          />

          <FormAutocomplete
            control={control}
            name='sectionCode'
            label='Shelf'
            placeholder='Select shelf'
            options={shelfOptions}
            onChange={handleSectionCodeChange}
            disabled={!aisleCode || isFetching}
            noOptionsText='No shelves'
            required
            data-testid='override-destination-shelf-select'
          />

          <FormAutocomplete
            control={control}
            name='binNumber'
            label='Bin'
            placeholder='Select bin'
            options={binOptions}
            disabled={!sectionCode || isFetching}
            noOptionsText='No bins'
            required
            data-testid='override-destination-bin-select'
          />
        </DialogContent>

        <DialogActions className='flex w-full justify-end gap-4 px-10 pb-6'>
          <Button
            appearance='outlined'
            variant='primary'
            className={classNames({ "flex-1": isTabletView })}
            onClick={handleClose}
            data-testid='cancel-override-button'
          >
            Cancel
          </Button>
          <Button
            type='submit'
            appearance='contained'
            variant='primary'
            className={classNames({ "flex-1": isTabletView })}
            disabled={!isValid}
            data-testid='confirm-override-button'
          >
            Confirm Override
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
